(() => {
  "use strict";

  const MAX_NATIVE_BYTES = 19 * 1024 * 1024;
  const MAX_NATIVE_PIXELS = 19 * 1000 * 1000;
  const PREVIEW_MAX_BYTES = 7 * 1024 * 1024;
  const PREVIEW_EDGES = [2400, 2000, 1600, 1200];
  const PREVIEW_QUALITIES = [0.84, 0.76, 0.68, 0.58];
  const DIRECT_UPLOAD_ENDPOINT = "https://vmwyvemuyfcipzcnqxzs.supabase.co/functions/v1/print-artwork-upload";
  const states = new WeakMap();

  function qs(root, selector) {
    return root.querySelector(selector);
  }

  function qsa(root, selector) {
    return Array.from(root.querySelectorAll(selector));
  }

  function formatBytes(bytes) {
    if (!Number.isFinite(bytes)) return "";
    if (bytes >= 1024 * 1024) return (bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 1 : 2) + " MB";
    return Math.max(1, Math.round(bytes / 1024)) + " KB";
  }

  function isLikelyImage(file) {
    return !!file && (String(file.type || "").startsWith("image/") || /\.(avif|bmp|gif|heic|heif|jpe?g|png|psd|svg|tiff?|webp)$/i.test(file.name || ""));
  }

  function validUrl(value) {
    try {
      const url = new URL(String(value || "").trim());
      return url.protocol === "https:" || url.protocol === "http:";
    } catch (error) {
      return false;
    }
  }

  function setStatus(studio, message, tone) {
    const status = qs(studio, "[data-upload-safety-status]");
    if (!status) return;
    status.textContent = message || "";
    status.setAttribute("data-tone", tone || "neutral");
  }

  function ensureStyles() {
    if (document.getElementById("print-studio-upload-safety-styles")) return;

    const style = document.createElement("style");
    style.id = "print-studio-upload-safety-styles";
    style.textContent = `
      .print-studio__upload-safety{display:none;margin-top:12px;padding:13px;border:1px solid var(--line);background:#fff}
      .print-studio__upload-safety.is-active{display:grid;gap:10px}
      .print-studio__upload-safety p{margin:0;color:var(--muted);font-size:.9rem;line-height:1.4}
      .print-studio__upload-safety p[data-tone=good]{color:#27643a}
      .print-studio__upload-safety p[data-tone=warn]{color:#8a4b12}
      .print-studio__upload-safety p[data-tone=error]{color:#9d2f20}
      .print-studio__production-link{display:none;gap:6px}
      .print-studio__production-link.is-active{display:grid}
      .print-studio__production-link span{font-weight:900}
      .print-studio__production-link input{width:100%;min-height:42px;box-sizing:border-box;border:1px solid var(--line);padding:9px 10px;font:inherit}
      .print-studio__production-link small{color:var(--muted);line-height:1.35}
      @media(max-width:700px){.print-studio__upload-safety{margin:10px 0 0;padding:11px}.print-studio__upload-safety p{font-size:.82rem}.print-studio__production-link input{min-height:38px}}
    `;
    document.head.append(style);
  }

  function ensureInput(form, selector, type, name) {
    let input = qs(form, selector);
    if (!input) {
      input = document.createElement("input");
      input.type = type;
      input.setAttribute(selector.slice(1, -1), "");
      form.append(input);
    }
    if (name) input.name = name;
    return input;
  }

  function ensureUi(studio) {
    ensureStyles();

    const panel = qs(studio, '[data-step="1"]');
    const upload = qs(studio, "[data-upload]");
    if (!panel || !upload) return null;

    let ui = qs(studio, "[data-upload-safety]");
    if (!ui) {
      ui = document.createElement("div");
      ui.className = "print-studio__upload-safety";
      ui.setAttribute("data-upload-safety", "");
      ui.innerHTML = [
        '<p data-upload-safety-status></p>',
        '<label class="print-studio__production-link" data-production-link-wrap>',
        "<span>Production file link</span>",
        '<input type="url" inputmode="url" autocomplete="url" placeholder="https://drive.google.com/... or https://wetransfer.com/..." data-production-link>',
        "<small>Required only when the original artwork is too large for Shopify's native checkout upload.</small>",
        "</label>"
      ].join("");
      const label = upload.closest(".print-studio__upload") || upload;
      label.insertAdjacentElement("afterend", ui);
    }

    const link = qs(ui, "[data-production-link]");
    if (link && !link.dataset.uploadSafetyBound) {
      link.dataset.uploadSafetyBound = "true";
      link.addEventListener("input", () => syncStateToForm(studio));
    }

    return ui;
  }

  function stateFor(studio) {
    let state = states.get(studio);
    if (!state) {
      state = {
        dimensions: null,
        file: null,
        oversized: false,
        previewFile: null,
        processing: false,
        error: "",
        uploadingOriginal: false,
        productionUrl: "",
        productionPath: "",
        productionExpiresAt: "",
        productionUploadError: "",
      };
      states.set(studio, state);
    }
    return state;
  }

  function setNativeUploadMode(studio, mode) {
    const input = qs(studio, "[data-upload]");
    if (!input) return;

    if (mode === "original") {
      input.name = "properties[Artwork file]";
      input.removeAttribute("form");
    } else {
      input.removeAttribute("name");
      input.setAttribute("form", "print-studio-preview-only");
    }
  }

  function setFileInputFiles(input, file) {
    if (!input || !file || typeof DataTransfer === "undefined") return false;
    const transfer = new DataTransfer();
    transfer.items.add(file);
    input.files = transfer.files;
    return input.files && input.files.length === 1;
  }

  function imageBitmapFor(file) {
    if (window.createImageBitmap) return createImageBitmap(file);
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Image could not be decoded."));
      };
      img.src = url;
    });
  }

  function canvasBlob(canvas, type, quality) {
    return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
  }

  async function measureImage(file) {
    if (!isLikelyImage(file) || /\.tiff?$/i.test(file.name || "")) return null;
    const bitmap = await imageBitmapFor(file);
    const dimensions = { width: bitmap.width || 0, height: bitmap.height || 0 };
    if (typeof bitmap.close === "function") bitmap.close();
    return dimensions.width && dimensions.height ? dimensions : null;
  }

  async function generatePreview(file) {
    const bitmap = await imageBitmapFor(file);
    const width = bitmap.width || 0;
    const height = bitmap.height || 0;
    if (!width || !height) throw new Error("Image dimensions could not be read.");

    for (const maxEdge of PREVIEW_EDGES) {
      const scale = Math.min(1, maxEdge / Math.max(width, height));
      const targetWidth = Math.max(1, Math.round(width * scale));
      const targetHeight = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const context = canvas.getContext("2d", { alpha: false });
      context.fillStyle = "#fff";
      context.fillRect(0, 0, targetWidth, targetHeight);
      context.drawImage(bitmap, 0, 0, targetWidth, targetHeight);

      for (const quality of PREVIEW_QUALITIES) {
        const blob = await canvasBlob(canvas, "image/jpeg", quality);
        if (!blob) continue;
        if (blob.size <= PREVIEW_MAX_BYTES || maxEdge === PREVIEW_EDGES[PREVIEW_EDGES.length - 1]) {
          if (typeof bitmap.close === "function") bitmap.close();
          const baseName = String(file.name || "artwork").replace(/\.[^.]+$/, "");
          return {
            file: new File([blob], baseName + "-order-preview.jpg", { type: "image/jpeg" }),
            originalWidth: width,
            originalHeight: height,
            width: targetWidth,
            height: targetHeight,
          };
        }
      }
    }

    if (typeof bitmap.close === "function") bitmap.close();
    throw new Error("Preview could not be generated.");
  }

  function showPreview(studio, file) {
    const image = qs(studio, "[data-art-image]");
    const art = qs(studio, "[data-art]");
    if (!image || !art || !file || !isLikelyImage(file) || /\.tiff?$/i.test(file.name || "")) return;
    const url = URL.createObjectURL(file);
    image.onload = () => URL.revokeObjectURL(url);
    image.src = url;
    art.classList.add("has-image");
  }

  function syncArtworkDimensions(studio, dimensions) {
    if (!studio || !dimensions || !dimensions.width || !dimensions.height) return;

    studio.artRatio = dimensions.width / dimensions.height;
    studio.artNaturalWidth = dimensions.width;
    studio.artNaturalHeight = dimensions.height;

    if (typeof studio.renderSizes === "function") studio.renderSizes();
    if (typeof studio.updatePreview === "function") studio.updatePreview();
    if (typeof studio.sync === "function") studio.sync();
    if (typeof studio.updateFitCopy === "function") studio.updateFitCopy();
  }

  async function postUploadService(payload) {
    const response = await fetch(DIRECT_UPLOAD_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || "Artwork upload service failed.");
    return data;
  }

  async function uploadOriginalFile(studio, file) {
    const state = stateFor(studio);
    if (!file) return false;

    state.uploadingOriginal = true;
    state.productionUploadError = "";
    state.productionUrl = "";
    state.productionPath = "";
    state.productionExpiresAt = "";
    syncStateToForm(studio);
    setStatus(studio, "Uploading the full-resolution artwork for production...", "warn");

    try {
      const ticket = await postUploadService({
        filename: file.name || "artwork",
        contentType: file.type || "application/octet-stream",
        size: file.size || 0,
        dimensions: state.dimensions,
      });
      const uploadResponse = await fetch(ticket.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!uploadResponse.ok) {
        const message = await uploadResponse.text().catch(() => "");
        throw new Error(message || "The production artwork upload did not finish.");
      }

      const completed = await postUploadService({
        action: "complete",
        path: ticket.path,
        filename: file.name || "artwork",
        contentType: file.type || "application/octet-stream",
      });
      state.productionUrl = completed.productionUrl || "";
      state.productionPath = completed.path || ticket.path || "";
      state.productionExpiresAt = completed.expiresAt || "";
      setStatus(studio, "Original artwork uploaded for production. A checkout-safe preview will attach to the order.", "good");
      return true;
    } catch (error) {
      state.productionUploadError = error && error.message ? error.message : "The production artwork upload failed.";
      setStatus(studio, state.previewFile ? "Preview is ready, but the original did not upload automatically. Paste a production-file link below before checkout." : "The original did not upload automatically. Paste a production-file link below before checkout.", "error");
      return false;
    } finally {
      state.uploadingOriginal = false;
      syncStateToForm(studio);
    }
  }

  function syncStateToForm(studio) {
    const form = qs(studio, "[data-form]");
    const ui = ensureUi(studio);
    const state = stateFor(studio);
    if (!form || !ui) return;

    const previewInput = ensureInput(form, "[data-upload-preview-file]", "file", "");
    previewInput.hidden = true;
    previewInput.style.display = "none";

    const mode = ensureInput(form, "[data-upload-mode]", "hidden", "properties[Artwork upload mode]");
    const fileName = ensureInput(form, "[data-original-file-name]", "hidden", "properties[Original file name]");
    const fileSize = ensureInput(form, "[data-original-file-size]", "hidden", "properties[Original file size]");
    const imageSize = ensureInput(form, "[data-original-image-size]", "hidden", "properties[Original image dimensions]");
    const productionLinkHidden = ensureInput(form, "[data-production-link-property]", "hidden", "");
    const storagePath = ensureInput(form, "[data-production-storage-path]", "hidden", "");
    const linkExpires = ensureInput(form, "[data-production-link-expires]", "hidden", "");
    const linkWrap = qs(ui, "[data-production-link-wrap]");
    const linkInput = qs(ui, "[data-production-link]");
    const linkValue = linkInput ? linkInput.value.trim() : "";

    if (!state.file) {
      ui.classList.remove("is-active");
      if (linkWrap) linkWrap.classList.remove("is-active");
      setNativeUploadMode(studio, "original");
      [previewInput, mode, fileName, fileSize, imageSize, productionLinkHidden, storagePath, linkExpires].forEach((input) => {
        input.removeAttribute("name");
        input.value = "";
      });
      return;
    }

    ui.classList.add("is-active");
    fileName.value = state.file.name || "";
    fileSize.value = formatBytes(state.file.size);
    imageSize.value = state.dimensions ? state.dimensions.width + " x " + state.dimensions.height + " px" : "Not available";

    if (state.oversized) {
      setNativeUploadMode(studio, "preview");
      mode.value = state.productionUrl ? "Preview attached; original uploaded for production" : state.previewFile ? "Preview attached; production file upload pending" : "Production file upload pending";
      if (state.previewFile) {
        previewInput.name = "properties[Artwork preview file]";
        setFileInputFiles(previewInput, state.previewFile);
      } else {
        previewInput.removeAttribute("name");
      }
      productionLinkHidden.name = "properties[Production file link]";
      productionLinkHidden.value = state.productionUrl || linkValue;
      if (state.productionPath) {
        storagePath.name = "properties[Artwork storage path]";
        storagePath.value = state.productionPath;
      } else {
        storagePath.removeAttribute("name");
        storagePath.value = "";
      }
      if (state.productionExpiresAt) {
        linkExpires.name = "properties[Production link expires]";
        linkExpires.value = state.productionExpiresAt;
      } else {
        linkExpires.removeAttribute("name");
        linkExpires.value = "";
      }
      if (linkWrap) linkWrap.classList.toggle("is-active", !validUrl(state.productionUrl));
    } else {
      setNativeUploadMode(studio, "original");
      mode.value = "Original file attached";
      previewInput.removeAttribute("name");
      productionLinkHidden.removeAttribute("name");
      productionLinkHidden.value = "";
      storagePath.removeAttribute("name");
      storagePath.value = "";
      linkExpires.removeAttribute("name");
      linkExpires.value = "";
      if (linkWrap) linkWrap.classList.remove("is-active");
    }
  }

  async function handleUploadChange(studio, event) {
    if (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    const input = qs(studio, "[data-upload]");
    const state = stateFor(studio);
    const file = input && input.files ? input.files[0] : null;
    state.file = file || null;
    state.dimensions = null;
    state.oversized = false;
    state.previewFile = null;
    state.processing = false;
    state.error = "";
    state.uploadingOriginal = false;
    state.productionUrl = "";
    state.productionPath = "";
    state.productionExpiresAt = "";
    state.productionUploadError = "";

    ensureUi(studio);
    syncStateToForm(studio);

    if (!file) {
      setStatus(studio, "", "neutral");
      return;
    }

    if (!isLikelyImage(file)) {
      state.oversized = file.size > MAX_NATIVE_BYTES;
      setStatus(studio, state.oversized ? "This file is too large for Shopify checkout. Add a production file link before checkout." : "File selected: " + file.name + " (" + formatBytes(file.size) + ").", state.oversized ? "warn" : "good");
      syncStateToForm(studio);
      return;
    }

    state.processing = true;
    setStatus(studio, "Checking artwork file...", "neutral");

    try {
      const fileTooLarge = file.size > MAX_NATIVE_BYTES;
      if (!fileTooLarge) state.dimensions = await measureImage(file);
      const pixels = state.dimensions ? state.dimensions.width * state.dimensions.height : 0;
      state.oversized = fileTooLarge || pixels > MAX_NATIVE_PIXELS;

      if (!state.oversized) {
        showPreview(studio, file);
        syncArtworkDimensions(studio, state.dimensions);
        setStatus(studio, "Original file ready to attach: " + formatBytes(file.size) + (state.dimensions ? ", " + state.dimensions.width + " x " + state.dimensions.height + " px." : "."), "good");
        return;
      }

      setStatus(studio, "Large file detected. Preparing a checkout-safe preview...", "warn");
      try {
        const preview = await generatePreview(file);
        state.previewFile = preview.file;
        state.dimensions = { width: preview.originalWidth, height: preview.originalHeight };
        showPreview(studio, preview.file);
        syncArtworkDimensions(studio, state.dimensions);
        setStatus(studio, "Preview attached for the order (" + formatBytes(preview.file.size) + "). Uploading the full-resolution original...", "warn");
      } catch (previewError) {
        state.error = previewError && previewError.message ? previewError.message : "Preview generation failed.";
        setStatus(studio, "This file is too large for Shopify checkout. Uploading the full-resolution original for production...", "warn");
      }
      await uploadOriginalFile(studio, file);
    } catch (error) {
      state.error = error && error.message ? error.message : "Preview generation failed.";
      state.oversized = file.size > MAX_NATIVE_BYTES;
      setStatus(studio, state.oversized ? "This file is too large for Shopify checkout and a browser preview could not be generated. Add a production-file link before checkout." : "File selected. Browser preview was not available, but Shopify can attach this file.", state.oversized ? "error" : "good");
    } finally {
      state.processing = false;
      syncStateToForm(studio);
    }
  }

  function blockSubmitIfNeeded(studio, event) {
    const state = stateFor(studio);
    if (!state.file) return;

    syncStateToForm(studio);
    if (!state.oversized) return;

    const linkInput = qs(studio, "[data-production-link]");
    const linkValue = linkInput ? linkInput.value.trim() : "";
    const hasProductionUrl = validUrl(state.productionUrl);
    const hasManualLink = validUrl(linkValue);
    if (state.processing) {
      event.preventDefault();
      setStatus(studio, "Still preparing the artwork upload. Try checkout again in a moment.", "warn");
      return;
    }

    if (!state.previewFile && !hasProductionUrl && !hasManualLink && isLikelyImage(state.file) && !/\.tiff?$/i.test(state.file.name || "")) {
      event.preventDefault();
      setStatus(studio, "The preview did not finish generating. Re-select the artwork or add a production-file link and contact the studio.", "error");
      return;
    }

    if (!hasProductionUrl && !hasManualLink) {
      event.preventDefault();
      setStatus(studio, "Paste a valid production-file link before checkout so the full-resolution artwork is attached to the order.", "error");
      if (linkInput) linkInput.focus();
    }
  }

  function install(studio) {
    if (!studio || studio.dataset.uploadSafetyInstalled === "true") return;
    const upload = qs(studio, "[data-upload]");
    const form = qs(studio, "[data-form]");
    if (!upload || !form) return;

    studio.dataset.uploadSafetyInstalled = "true";
    ensureUi(studio);
    upload.addEventListener("change", (event) => handleUploadChange(studio, event), true);
    form.addEventListener("submit", (event) => blockSubmitIfNeeded(studio, event), true);
    syncStateToForm(studio);
  }

  function init() {
    qsa(document, "print-frame-studio").forEach(install);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();

  if (window.customElements) customElements.whenDefined("print-frame-studio").then(init);
})();
